import React from 'react';
import { motion } from 'framer-motion';
import { ButtonProps } from '../../types';

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  children,
  onClick,
  type = 'button',
  className = '',
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden min-h-[48px]';

  const variantClasses = {
    primary: 'text-white bg-primary-500 hover:bg-primary-600 focus:ring-accent-500 shadow-md hover:shadow-lg border border-transparent hover:border-warning-500',
    secondary: 'text-primary-500 bg-transparent border-2 border-primary-500 hover:bg-primary-500/5 focus:ring-accent-500 shadow-sm hover:shadow-md',
    outline: 'text-primary-500 bg-transparent border border-primary-500 hover:bg-primary-500/5 focus:ring-accent-500',
    ghost: 'text-primary-500/70 bg-transparent border border-transparent hover:bg-primary-500/5 hover:text-primary-500 focus:ring-accent-500/50',
    success: 'text-white bg-success-500 hover:opacity-90 focus:ring-accent-500 border border-transparent',
    danger: 'text-white bg-gradient-to-r from-error-600 to-error-700 hover:from-error-700 hover:to-error-800 focus:ring-error-500 shadow-md hover:shadow-lg border border-transparent'
  } as const;

  const sizeClasses = {
    sm: 'px-4 py-2 text-xs rounded-lg min-h-[44px]',
    md: 'px-6 py-3 text-sm rounded-xl min-h-[48px]',
    lg: 'px-8 py-4 text-base rounded-xl min-h-[48px]'
  } as const;

  const hoverClasses = !disabled && !loading ? 'hover:-translate-y-0.5' : '';
  const isDisabled = disabled || loading;

  return (
    <motion.button
      type={type}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${hoverClasses} ${className}`}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      whileHover={!isDisabled ? { y: -1 } : {}}
      whileTap={!isDisabled ? { scale: 0.98 } : {}}
      transition={{ duration: 0.1 }}
      {...props}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center rounded-md">
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      
      <span className={`inline-flex items-center gap-1.5 ${loading ? 'opacity-0' : 'opacity-100'}`} style={{ transition: 'opacity 0.2s ease-in-out' }}>
        {children}
      </span>
    </motion.button>
  );
};

export default Button; 